import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { money, DEMO_MONTH } from "../lib/format";
import { ChevronLeft, ChevronRight, Landmark } from "lucide-react";

// Responsive financial tile: on narrow phones it is a compact row (label left, value right)
// so the currency figure keeps its full intrinsic width and never clips; at >=sm it becomes a
// stacked block inside the 3-column grid. Structural fix for the on-device 3-col clipping.
function QTile({ tone, label, value }) {
  const bg = tone === "amber" ? "bg-amber-50" : "bg-[#E6F4F1]";
  const lc = tone === "amber" ? "text-amber-700" : "text-[#0D786A]";
  return (
    <div className={`rounded-xl ${bg} px-3 py-2 flex items-center justify-between gap-3 sm:block sm:gap-0`}>
      <div className={`text-[10px] ${lc} font-semibold uppercase tracking-wide min-w-0 sm:mb-0.5`}>{label}</div>
      <div className="font-num font-semibold text-slate-900 text-sm text-right sm:text-left tabular-nums whitespace-nowrap">{value}</div>
    </div>
  );
}

export default function BAS() {
  const navigate = useNavigate();
  const [startYear, setStartYear] = useState(2026); // FY start year (Jul)
  const ref = `${startYear}-09-15`;

  const { data, isLoading } = useQuery({
    queryKey: ["bas", startYear],
    queryFn: async () => (await api.get(`/bas?ref=${ref}`)).data,
  });

  const fy = data?.financial_year;
  const totals = data?.fy_totals;

  return (
    <AppShell>
      <PageHeader title="BAS & GST" subtitle="Quarterly GST summary" onBack={() => navigate("/more")} />
      <div className="px-5 pb-24 df-fade-in space-y-4">
        {/* FY selector */}
        <div className="df-card flex items-center justify-between px-2 py-2">
          <button data-testid="bas-fy-prev" onClick={() => setStartYear((y) => y - 1)}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronLeft size={20} className="text-slate-600" />
          </button>
          <span data-testid="bas-fy-label" className="font-heading font-semibold text-slate-900">
            {fy?.label || `FY ${startYear}/${String((startYear + 1) % 100).padStart(2, "0")}`}
          </span>
          <button data-testid="bas-fy-next" onClick={() => setStartYear((y) => y + 1)}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronRight size={20} className="text-slate-600" />
          </button>
        </div>

        {/* FY net total */}
        <div className="df-card p-5" data-testid="bas-fy-total">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-500">Estimated net GST (year)</span>
            <span className="h-9 w-9 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center">
              <Landmark size={18} />
            </span>
          </div>
          <div className="mt-2 font-num font-bold text-4xl tracking-tight text-slate-900">
            {isLoading ? "—" : money(totals?.net_cents)}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="min-w-0">1A GST on sales <b className="font-num text-slate-700">{money(totals?.a1_gst_on_sales_cents)}</b></span>
            <span className="min-w-0">1B GST on purchases <b className="font-num text-slate-700">{money(totals?.b1_gst_on_purchases_cents)}</b></span>
          </div>
        </div>

        {/* Quarter cards */}
        <div className="space-y-3" data-testid="bas-quarters">
          {(data?.quarters || []).map((q) => (
            <div key={q.quarter} className="df-card p-4" data-testid={`bas-quarter-${q.quarter}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading font-semibold text-slate-900">{q.label}</div>
                  <div className="text-xs text-slate-400">{q.start_fmt} – {q.end_fmt}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Net GST (1A − 1B)</div>
                  <div className={`font-num font-bold text-lg ${q.net_cents < 0 ? "text-emerald-700" : "text-slate-900"}`}>
                    {money(q.net_cents)}
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <QTile tone="teal" label="G1 Total sales" value={money(q.g1_total_sales_cents)} />
                <QTile tone="teal" label="1A GST sales" value={money(q.a1_gst_on_sales_cents)} />
                <QTile tone="amber" label="1B GST purchases" value={money(q.b1_gst_on_purchases_cents)} />
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-y-1 text-[11px] text-slate-400">
                <span>Facility / Service Fee GST <b className="font-num">{money(q.facilities_fee_gst_cents)}</b> <span className="text-slate-400">(included in 1B)</span></span>
                {q.review_count > 0 && <span className="text-amber-600 font-medium" data-testid={`bas-review-${q.quarter}`}>{q.review_count} item(s) to review</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-[11px] text-amber-800 leading-relaxed" data-testid="bas-facility-warning">
          Facility/service-fee GST is automatically included in 1B. Do not enter the same facility/service fee again as a BAS-claimable expense, as this may double-count GST.
        </div>

        <p className="text-xs text-slate-400 leading-relaxed px-1" data-testid="bas-note">{data?.note}</p>
      </div>
    </AppShell>
  );
}
